/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: ObjectBaseComponent
 * Reason: Object component for cr.CustomerInsurableObject (update mode). Three navigation sections
 *         - the assignment itself, the linked ci.InsurableObject with its dynamic generic-template
 *         fields, and a conditional editable ad.Address for an object specific address.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';

import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {DtoDetail} from '../../../../../core/shared/dto/dto.detail';
import {DtoImportObjectContext} from '../../../../../core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from '../../../../../core/shared/dto/dto.import.object.context.entry';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {ObjectChangeInformation} from '../../../../../core/shared/logic/object-change-information';

// cr -> ci is the ESTABLISHED, allowed import direction (design section 15.2); only the reverse
// edge is forbidden (gate condition GC1). These are therefore plain runtime imports.
import {InsurableObjectDto} from '../../../../ci/model/dto/entity/insurable-object.dto';
import {InsurableObjectTypeDto} from '../../../../ci/model/dto/entity/insurable-object-type.dto';
import {InsurableObjectService} from '../../../../ci/service/api/insurable-object.service';
import {InsurableObjectTypeService} from '../../../../ci/service/api/insurable-object-type.service';
import {
    CiInsurableObjectAddressFieldService
} from '../../../../ci/service/domain/ci-insurable-object-address-field.service';
import {
    CiInsurableObjectAddress,
    CiInsurableObjectAddressResolver
} from '../../../../ci/logic/ci-insurable-object-address';
import {CI_ADDRESS_OBJECT_TYPE_ALIAS} from '../../../../ci/logic/ci-insurable-object-address-field';

import {CustomerInsurableObjectDto} from '../../../model/dto/entity/customer-insurable-object.dto';

/** cc.ObjectType alias of the customer, the entry the backend form scoping reads. */
const OBJECT_TYPE_CUSTOMER = 'cr.Customer';

/**
 * Object component for {@code cr.CustomerInsurableObject}, update mode.
 *
 * Registered through the NATIVE objectComponent registry of cr.entity-provider (gate condition
 * GC8) together with the guided create component for mode=create. That registration REPLACES the
 * former service override on CustomerInsurableObjectService, which pointed both modes at the two
 * cm components; those stay as code but are no longer reached from this alias (design section
 * 15.3 default 5). The old wizard remains registered for ci.ContractInsurableObject.
 *
 * TAB MECHANISM (gate condition GC18): the shared {@code <object-component-navigation-side>} icon
 * rail driven by native NavigationItems - same decision and same reasoning as
 * CiInsurableObjectObjectComponent. The items are deliberately NOT emitted through
 * onNavigationItems (hence the OBC checker's G5.2 warning): mvs-object/object.component.html
 * renders its own rail as soon as navigationItems arrive, which would double the chrome.
 */
@Component({
    selector: 'cr-customer-insurable-object-object',
    templateUrl: './cr-customer-insurable-object-object.component.html',
    styleUrls: ['./cr-customer-insurable-object-object.component.scss'],
    standalone: false
})
export class CrCustomerInsurableObjectObjectComponent
    extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    /** Sections of the icon rail. "Adresse" is appended only when it applies. */
    navigationItems: NavigationItem[] = [];

    /** The section currently shown; two-way bound by the shared rail. */
    activeNavigationItem: NavigationItem;

    /** The linked object - loaded separately, the assignment only carries its id. */
    insurableObject: InsurableObjectDto;

    /** The object's type, for the header icon and name. */
    type: InsurableObjectTypeDto;

    /** Resolved address situation - decides whether the "Adresse" section exists at all. */
    address: CiInsurableObjectAddress;

    /** Context handed to the embedded forms so the backend scopes its value lists to the customer. */
    formImportObjectContext: DtoImportObjectContext;

    objectBusy: boolean = false;
    objectError: string;

    protected subscription: Subscription = new Subscription();

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        protected insurableObjectService: InsurableObjectService,
        protected insurableObjectTypeService: InsurableObjectTypeService,
        protected addressFieldService: CiInsurableObjectAddressFieldService) {

        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.subscription.unsubscribe();
        super.ngOnDestroy();
    }

    /**
     * Method called whenever the object changed.
     *
     * NEVER calls refreshObject() - that would re-enter this hook forever.
     */
    onObjectChanged(): void {

        if (!this.dto) {
            return;
        }

        this.rebuildNavigationItems();
        this.refreshFormImportObjectContext();
        this.loadInsurableObject();
    }

    // ---------------------------------------------------------------------------------------- //
    // Navigation
    // ---------------------------------------------------------------------------------------- //

    protected rebuildNavigationItems(): void {

        const items: NavigationItem[] = [
            {
                label: 'Zuordnung',
                action: 'assignment',
                icon: 'fa-regular fa-link',
                tooltip: 'Zuordnung des Objekts zum Kunden',
                toggleable: false,
                default: true
            },
            {
                label: 'Objekt',
                action: 'object',
                icon: 'fa-regular fa-cube',
                tooltip: 'Daten des versicherbaren Objekts',
                toggleable: false,
                default: false
            }
        ];

        if (this.address?.objectSpecific) {
            items.push({
                label: this.address.addressField?.name || 'Adresse',
                action: 'address',
                icon: 'fa-regular fa-location-dot',
                tooltip: 'Objektspezifische Adresse dieses Objekts',
                toggleable: false,
                default: false
            });
        }

        this.navigationItems = items;

        if (!this.activeNavigationItem) {
            this.activeNavigationItem = items[0];
            return;
        }

        // The rail holds a reference into the OLD array; re-point it so the active section survives
        // a rebuild (and falls back to the first one when its section just disappeared).
        const stillPresent = items.find(item => item.action === this.activeNavigationItem.action);
        this.activeNavigationItem = stillPresent ?? items[0];
    }

    // No explicit rail handler: the [(activeNavigationItem)] two-way binding already writes the
    // selection back, and the rail keeps the URL in sync through the sideNavItem query parameter.

    isSectionActive(action: string): boolean {
        return this.activeNavigationItem?.action === action;
    }

    // ---------------------------------------------------------------------------------------- //
    // The linked object
    // ---------------------------------------------------------------------------------------- //

    /**
     * Load the ci.InsurableObject behind the assignment.
     *
     * Needed in full, not just by id: the header reads formatName and typeDtoId off it, and the
     * address resolution reads its generic gen_<typeFieldId>DtoId value.
     */
    protected loadInsurableObject(): void {

        const insurableObjectId = this.getInsurableObjectId();

        if (!insurableObjectId) {
            this.insurableObject = null;
            this.objectError = 'Die Zuordnung verweist auf kein versicherbares Objekt.';
            return;
        }

        this.objectBusy = true;
        this.objectError = null;

        this.subscription.add(
            this.insurableObjectService.get(insurableObjectId).subscribe({
                next: (dto: DtoDetail) => {

                    this.insurableObject = dto as InsurableObjectDto;
                    this.objectBusy = false;

                    this.setPageTitle(this.getObjectLabel());
                    this.refreshType();
                    this.refreshAddress();
                },
                error: () => {
                    this.insurableObject = null;
                    this.objectBusy = false;
                    this.objectError = 'Das versicherbare Objekt konnte nicht geladen werden.';
                }
            })
        );
    }

    getInsurableObjectId(): number {
        return (this.dto as CustomerInsurableObjectDto)?.insurableObjectDtoId;
    }

    getCustomerId(): number {
        return (this.dto as CustomerInsurableObjectDto)?.customerDtoId;
    }

    // ---------------------------------------------------------------------------------------- //
    // Header
    // ---------------------------------------------------------------------------------------- //

    /**
     * The object's type, for the header icon. Read from the cached full type list rather than from
     * the DTO, because a resolved typeDto does not reliably carry image/rgbaColor.
     */
    protected refreshType(): void {

        const typeId = this.insurableObject?.typeDtoId;

        if (typeId == null) {
            this.type = null;
            return;
        }

        this.subscription.add(
            this.insurableObjectTypeService.listAllTypes().subscribe({
                next: (types: InsurableObjectTypeDto[]) =>
                    this.type = (types || []).find(entry => entry?.id === typeId),
                error: () => this.type = null
            })
        );
    }

    /** formatName || description || '<Typ> #<id>'. */
    getObjectLabel(): string {

        if (this.insurableObject?.formatName) {
            return this.insurableObject.formatName;
        }

        if (this.insurableObject?.description) {
            return this.insurableObject.description;
        }

        const typeName = this.type?.name;
        const objectId = this.getInsurableObjectId();

        return typeName ? `${typeName} #${objectId}` : `Objekt #${objectId}`;
    }

    getTypeIcon(): string {
        return this.type?.image || '';
    }

    /** rgbaColor holds a theme variable NAME, never a literal colour. */
    getTypeIconStyle(): { [key: string]: string } {

        const color = this.type?.rgbaColor;

        return color ? {'color': 'var(--' + color + ')'} : {};
    }

    getTypeName(): string {
        return this.type?.name || this.insurableObject?.typeDtoName || '';
    }

    getCustomerLabel(): string {
        return this.dto?.['customerDtoName'] || ('Kunde #' + this.getCustomerId());
    }

    // ---------------------------------------------------------------------------------------- //
    // Adresse
    // ---------------------------------------------------------------------------------------- //

    /**
     * Decide whether the linked object carries an address of its own, and rebuild the rail.
     *
     * Same shared resolution the ci object component uses, so both components answer the question
     * identically.
     */
    protected refreshAddress(): void {

        this.subscription.add(
            CiInsurableObjectAddressResolver.resolve(
                this.addressFieldService, this.coreService, this.insurableObject, this.getCustomerId()
            ).subscribe({
                next: (address: CiInsurableObjectAddress) => {
                    this.address = address;
                    this.rebuildNavigationItems();
                },
                error: () => {
                    // A failed classification hides the section rather than showing a broken one.
                    this.address = null;
                    this.rebuildNavigationItems();
                }
            })
        );
    }

    getAddressId(): number {
        return this.address?.addressId;
    }

    /** The address form edits a plain ad.Address row. */
    getAddressObjectType(): string {
        return CI_ADDRESS_OBJECT_TYPE_ALIAS;
    }

    // ---------------------------------------------------------------------------------------- //
    // Embedded forms
    // ---------------------------------------------------------------------------------------- //

    /**
     * Merge the ambient context with the customer of this assignment.
     *
     * The backend InsurableObjectForm scopes its dynamic address and person value lists to the
     * customer taken from this context. Our entry wins over a caller supplied one, and the input
     * context is never mutated.
     */
    protected refreshFormImportObjectContext(): void {

        const customerId = this.getCustomerId();
        const context = this.importObjectContext
            ? this.importObjectContext.copy()
            : DtoImportObjectContext.createEmpty();

        if (customerId) {
            context.addEntryAndReplaceType(new DtoImportObjectContextEntry(OBJECT_TYPE_CUSTOMER, customerId));
        }

        this.formImportObjectContext = context;
    }

    /**
     * The assignment form saved: pass the event on and re-read the assignment, because a changed
     * customer changes both the form context and the address classification.
     */
    handleChangedAssignment(event: ObjectChangeInformation): void {

        this.onChangedObject.emit(event);

        // Allowed here: this is an event handler, NOT onObjectChanged - no loop.
        this.refreshObject(false);
    }

    /**
     * The object or address form saved. The event is passed on unchanged (the object component
     * contract); the object is re-read so header, type and address stay in sync.
     */
    handleChangedInsurableObject(event: ObjectChangeInformation): void {

        this.onChangedObject.emit(event);
        this.loadInsurableObject();
    }

}
